import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useReducedMotion } from 'framer-motion';
import {
  BriefcaseBusiness,
  FolderKanban,
  HelpCircle,
  MessageCircle,
  Send,
  Sparkles,
  X,
} from 'lucide-react';

const sessionKey = 'astem-guided-assistant-shown';
const whatsappUrl =
  'https://wa.me/233247754531?text=Hi%20ASTEM%20Software%20Lab%2C%20I%20need%20assistance.';

const options = [
  {
    label: 'Explore Services',
    description: 'Review our solution portfolio.',
    to: '/services',
    icon: BriefcaseBusiness,
  },
  {
    label: 'View Projects',
    description: 'See selected delivery outcomes.',
    to: '/projects',
    icon: FolderKanban,
  },
  {
    label: 'Start a Consultation',
    description: 'Tell us what you need to build.',
    to: '/contact',
    icon: Send,
  },
  {
    label: 'Read FAQs',
    description: 'Find quick answers and guidance.',
    to: '/faqs',
    icon: HelpCircle,
  },
];

export default function GuidedAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [dragBounds, setDragBounds] = useState({ top: -140, bottom: 0 });
  const y = useMotionValue(0);
  const prefersReducedMotion = useReducedMotion();

  const openAssistant = () => {
    y.set(0);
    setIsOpen(true);
  };

  useEffect(() => {
    const updateDragBounds = () => {
      const top = -Math.min(180, Math.max(80, window.innerHeight * 0.28));
      setDragBounds({ top, bottom: 0 });

      if (y.get() < top) y.set(top);
    };

    updateDragBounds();
    window.addEventListener('resize', updateDragBounds);
    return () => window.removeEventListener('resize', updateDragBounds);
  }, [y]);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(sessionKey)) return undefined;
    } catch (error) {
      console.warn('sessionStorage not available:', error);
    }

    const handleScroll = () => {
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      const hasReachedTrigger = scrollableHeight > 0 && window.scrollY / scrollableHeight >= 0.25;

      if (!hasReachedTrigger) return;

      try {
        sessionStorage.setItem(sessionKey, 'true');
      } catch (error) {
        console.warn('sessionStorage not available:', error);
      }

      y.set(0);
      setIsOpen(true);
      window.removeEventListener('scroll', handleScroll);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [y]);

  return (
    <motion.div
      drag={isOpen ? false : 'y'}
      dragConstraints={dragBounds}
      dragElastic={0.04}
      dragMomentum={false}
      whileDrag={{ scale: 1.03 }}
      style={{ y }}
      className="fixed bottom-4 right-4 z-[60] flex flex-col items-end sm:bottom-6 sm:right-6"
    >
      {isOpen && (
        <section
          aria-label="ASTEM guided assistant"
          className="mb-2 flex max-h-[calc(100dvh-5rem)] w-[calc(100vw-2rem)] max-w-[18rem] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl shadow-slate-950/20"
        >
          <div className="flex shrink-0 items-center justify-between bg-slate-950 px-3 py-2 text-white">
            <div className="flex items-center gap-2">
              <div className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-warm/15 text-warm-amber ring-1 ring-warm/30">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-warm-amber">
                  ASTEM Connect
                </p>
                <p className="text-[11px] text-slate-300">How can we help?</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-300 transition hover:bg-white/10 hover:text-white"
              aria-label="Close assistant"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>

          <div className="min-h-0 touch-pan-y overflow-y-auto overscroll-contain p-2 [-webkit-overflow-scrolling:touch]">
            <p className="px-1 pb-1.5 text-[11px] font-bold leading-snug text-slate-600">
              Explore our capabilities or start a conversation with our team.
            </p>

            <div className="space-y-1">
              {options.map(option => {
                const Icon = option.icon;

                return (
                  <Link
                    key={option.label}
                    to={option.to}
                    onClick={() => setIsOpen(false)}
                    className="group flex items-center gap-2 rounded-lg border border-transparent px-1.5 py-1.5 transition hover:border-slate-200 hover:bg-slate-50"
                  >
                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-950 text-warm-amber transition group-hover:bg-primary">
                      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs font-black text-slate-900">{option.label}</span>
                      <span className="block text-[10px] leading-snug text-slate-500">
                        {option.description}
                      </span>
                    </span>
                  </Link>
                );
              })}
            </div>

            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1.5 flex items-center justify-center gap-1.5 rounded-full bg-warm px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.1em] text-white transition hover:bg-warm-terracotta"
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
              Chat on WhatsApp
            </a>
          </div>
        </section>
      )}

      {!isOpen && (
        <button
          type="button"
          onClick={openAssistant}
          aria-label="Open ASTEM assistant"
          title="Drag to reposition or click to chat"
          className="inline-flex h-10 cursor-grab items-center justify-center gap-1.5 rounded-full bg-slate-950 px-3.5 text-white shadow-xl shadow-slate-950/20 ring-1 ring-white/10 transition hover:bg-primary active:cursor-grabbing focus:outline-none focus:ring-4 focus:ring-warm/30"
        >
          <motion.span
            aria-hidden="true"
            animate={prefersReducedMotion ? undefined : {
              scale: [1, 1.22, 1, 1.16, 1],
              rotate: [0, -10, 10, -6, 0],
            }}
            transition={{ duration: 1.1, repeat: Infinity, repeatDelay: 2.2, ease: 'easeInOut' }}
            className="inline-flex"
          >
            <MessageCircle className="h-4.5 w-4.5 text-warm-amber" />
          </motion.span>
          <span className="text-[11px] font-black uppercase tracking-[0.12em]">Let's Chat</span>
        </button>
      )}
    </motion.div>
  );
}
